import { Fragment, useMemo } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import {
  useCollectionBinding,
  useContentToken,
  useProperty,
  useSlotGroupOverrides,
} from '@vibesites/runtime/react';
import { buildSitemapLinks, hasSiteMapImageOverride } from './_navShared';

interface VirtualTour {
  virtual_tour_url?: string | null;
}

export function SitemapContent() {
  // Mirror in-site navigation settings for the sitemap: same visibility /
  // eligibility as the header. Outbound-only links (Apply Now, Resident
  // Portal) stay off the list.
  const virtualTours = useCollectionBinding<VirtualTour>('sitemapcontent.virtual_tours', {
    collection: 'virtual_tours',
  });
  const hasVirtualTours = (virtualTours || []).some((t) => t.virtual_tour_url);

  // Interactive Map appears in the nav only when the property has the
  // ApartmentSync (Repli360 / RRAC) IDs to drive its widget, or an explicit
  // editor override on the header. Replicate that check so the sitemap doesn't
  // surface a page the nav hides. (Custom fields are read via useProperty; the
  // override token is header-scoped and read-only here.)
  const property = useProperty() as Record<string, unknown>;
  const apartmentSyncJsId = property?.custom_apartmentsync_javascript_id as
    | string
    | null
    | undefined;
  const apartmentSyncConfigId = property?.custom_apartmentsync_config_id as
    | string
    | null
    | undefined;
  const interactiveMapShowIfExists = useContentToken('header.interactive_map_show_if_exists');
  const hasInteractiveLeasing =
    (!!(apartmentSyncJsId && String(apartmentSyncJsId).trim()) &&
      !!(apartmentSyncConfigId && String(apartmentSyncConfigId).trim())) ||
    !!(interactiveMapShowIfExists && String(interactiveMapShowIfExists).trim());

  // Property Map — same gate as the header (SiteMapImage › image uploaded).
  const hasSiteMapImage = hasSiteMapImageOverride(useSlotGroupOverrides());

  const links = useMemo(
    () => buildSitemapLinks(hasVirtualTours, hasInteractiveLeasing, hasSiteMapImage),
    [hasVirtualTours, hasInteractiveLeasing, hasSiteMapImage],
  );

  // Flatten parent + children so nested nav items still appear on the sitemap.
  const flatLinks = useMemo(
    () =>
      links.flatMap((link) => (link.children?.length ? [link, ...link.children] : [link])),
    [links],
  );

  return (
    <section className="py-12 md:py-16 bg-[var(--vibe-color-background)]">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        {flatLinks.map((link, index) => {
          // Trim the space above the "Floor Plans" entry by 30px.
          const isFloorPlans = link.label.trim().toLowerCase() === 'floor plans';
          return (
            <Fragment key={`${link.href}-${link.label}`}>
              {index > 0 && (
                <hr className="mx-auto w-12 border-t border-[var(--vibe-color-border)]" />
              )}
              <a
                href={link.href}
                target={link.target || undefined}
                rel={link.target === '_blank' ? 'noopener noreferrer' : undefined}
                data-vibesite-event={link.event}
                className={`block text-center font-heading text-lg md:text-2xl font-normal text-[var(--vibe-color-text)] py-3 hover:opacity-80 transition-opacity${
                  isFloorPlans ? ' -mt-[30px]' : ''
                }`}
              >
                {link.label}
              </a>
            </Fragment>
          );
        })}
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'SitemapContent',
  displayName: 'Sitemap',
  tokens: {},
  collectionBindings: {
    'sitemapcontent.virtual_tours': {
      label: 'Virtual Tours',
      collection: 'virtual_tours',
    },
  },
};

(SitemapContent as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default SitemapContent;
